import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@icc/shared/config.service';
import { core } from '@icc/shared/helpers/b2b';
import { TaxRate } from '../interfaces/tax-rate.interface';

@Injectable({
    providedIn: 'root',
})
export class TaxRatesService {
    taxRates: TaxRate[] = [];

    constructor(private configService: ConfigService) {}

    static recalcPrice(
        targetGross: boolean,
        priceValue: number,
        isGross: boolean,
        priceTaxRate: number,
        targetTaxRate?: number
    ) {
        if (!priceValue) {
            return priceValue;
        }
        if (!targetGross) {
            if (isGross) {
                return TaxRatesService.recalcToNetPrice(priceValue, priceTaxRate);
            } else {
                return core.round(priceValue);
            }
        } else {
            if (isGross) {
                if (targetTaxRate == null || priceTaxRate === targetTaxRate) {
                    return core.round(priceValue);
                } else {
                    return TaxRatesService.recalcToGrossPrice(
                        TaxRatesService.recalcToNetPrice(priceValue, priceTaxRate),
                        targetTaxRate
                    );
                }
            } else {
                return TaxRatesService.recalcToGrossPrice(
                    priceValue,
                    targetTaxRate ?? priceTaxRate
                );
            }
        }
    }

    static recalcToGrossPrice(priceValue: number, priceTaxRate: number) {
        return core.round((priceValue * (100 + Number(priceTaxRate))) / 100);
    }

    static recalcToNetPrice(priceValue: number, priceTaxRate: number) {
        return core.round(priceValue / ((100 + Number(priceTaxRate)) / 100));
    }

    static isGross(documentMethod: 'netSummary' | 'netPosition' | 'grossSummary') {
        return documentMethod === 'grossSummary';
    }

    public async loadRates() {
        if (!this.taxRates?.length) {
            this.taxRates = this.configService.config.taxRates;
        }
    }

    public getRates() {
        return this.taxRates;
    }

    /**
     * Zwraca podatek VAT wg jego Id.
     *
     * @param taxRateId Id stawki podatku VAT, np. 2.
     * @returns Pasujący podatek VAT
     */
    public getTaxRate(taxRateId: number): TaxRate {
        return this.taxRates.find((tax) => Number(tax.id) === Number(taxRateId));
    }

    /**
     * Zwraca pierwszy pasujący podatek VAT wg jego wartości.
     * Jeśli takiego nie ma to zwraca domyślny podatek VAT dla aktualnej firmy.
     *
     * @param taxRate Stawka podatku VAT, np. 23.
     * @returns Pasujący podatek VAT
     */
    public matchTaxRate(taxRate?: number): TaxRate {
        return (
            this.taxRates.find((tax) => Number(tax.value) === Number(taxRate)) ??
            this.getDefaultTaxRate()
        );
    }

    public getDefaultTaxRate(): TaxRate {
        const taxRateId = this.configService.user.user.ActiveCompanyTradeSettings?.defaultTaxRateId;
        return this.getTaxRate(taxRateId) ?? this.taxRates[0];
    }

    public getBuyDefaultTaxRate(): TaxRate {
        const taxRateId = this.configService.user.user.ActiveCompanyTradeSettings
            ?.ownerDefaultTaxRateId;
        return this.getTaxRate(taxRateId) ?? this.taxRates[0];
    }

    public getTaxRateCalculationMethod(): 'netSummary' | 'netPosition' | 'grossSummary' {
        return (
            this.configService.user.user.ActiveCompanyTradeSettings?.taxRateCalculationMethod ??
            'netSummary'
        );
    }

    public getBuyTaxRateCalculationMethod(): 'netSummary' | 'netPosition' | 'grossSummary' {
        return (
            this.configService.user.user.ActiveCompanyTradeSettings
                ?.ownerTaxRateCalculationMethod ??
            this.getTaxRateCalculationMethod() ??
            'netSummary'
        );
    }
}
